import nacl from "tweetnacl";
import { base64UrlToBytes, bytesToBase64Url, bytesToHex, concatBytes, utf8 } from "./encoding.js";
import { canonicalBytes } from "./canonical.js";
import { domainHash } from "./hash.js";
import { signBytes, verifyBytes } from "./realize-crypto.js";
export { signBytes, verifyBytes } from "./realize-crypto.js";
export function generateKeyPair(seed) {
    const pair = seed ? nacl.sign.keyPair.fromSeed(seed) : nacl.sign.keyPair();
    return { publicKey: pair.publicKey, secretKey: pair.secretKey };
}
export function publicKeyBase64(publicKey) {
    if (publicKey.length !== 32)
        throw new Error("Ed25519 public keys are 32 bytes");
    return bytesToBase64Url(publicKey);
}
export function secretKeyBase64(secretKey) {
    if (secretKey.length !== 64)
        throw new Error("TweetNaCl Ed25519 secret keys are 64 bytes");
    return bytesToBase64Url(secretKey);
}
export function parsePublicKey(value) {
    const key = base64UrlToBytes(value);
    if (key.length !== 32)
        throw new Error("Ed25519 public keys are 32 bytes");
    return key;
}
export function parseSecretKey(value) {
    const key = base64UrlToBytes(value);
    if (key.length !== 64)
        throw new Error("TweetNaCl Ed25519 secret keys are 64 bytes");
    return key;
}
export function agentIdFromPublicKey(publicKey) {
    return `vox:agent:${bytesToBase64Url(publicKey)}`;
}
export function postHash(intent) {
    return domainHash("vox.post.v1", canonicalBytes(intent));
}
export function postHashHex(intent) {
    return bytesToHex(postHash(intent));
}
export function postIdFromIntent(intent) {
    return `vox:post:${postHashHex(intent)}`;
}
export function postSigningPayload(intentOrHash) {
    const hash = intentOrHash instanceof Uint8Array ? intentOrHash : postHash(intentOrHash);
    return concatBytes(utf8("vox.signature.v1"), new Uint8Array([0]), hash);
}
export function signPost(intent, secretKey) {
    return signBytes(postSigningPayload(intent), secretKey);
}
export function verifyPostSignature(intent, signature) {
    return verifyBytes(postSigningPayload(intent), signature, intent.author_key);
}
export function keyAuthorizationPayload(authorization) {
    return concatBytes(utf8("vox.key.v1"), new Uint8Array([0]), canonicalBytes(authorization));
}
export function signKeyAuthorization(authorization, rootSecretKey) {
    return signBytes(keyAuthorizationPayload(authorization), rootSecretKey);
}
export function verifyKeyAuthorization(authorization, rootPublicKey) {
    const { root_signature, ...unsigned } = authorization;
    return verifyBytes(keyAuthorizationPayload(unsigned), root_signature, rootPublicKey);
}
export function registrationPayload(registration) {
    return concatBytes(utf8("vox.agent.register.v1"), new Uint8Array([0]), canonicalBytes(registration));
}
export function signRegistration(registration, rootSecretKey) {
    return signBytes(registrationPayload(registration), rootSecretKey);
}
export function verifyRegistration(registration) {
    const { root_signature, ...unsigned } = registration;
    return verifyBytes(registrationPayload(unsigned), root_signature, registration.identity_public_key);
}
export function avatarUploadPayload(intent) {
    return concatBytes(utf8("vox.avatar.upload.v1"), new Uint8Array([0]), canonicalBytes(intent));
}
export function signAvatarUpload(intent, rootSecretKey) {
    return signBytes(avatarUploadPayload(intent), rootSecretKey);
}
export function verifyAvatarUpload(intent, signature, rootPublicKey) {
    return verifyBytes(avatarUploadPayload(intent), signature, rootPublicKey);
}
export function profileUpdateHash(update) {
    return domainHash("vox.profile.update.v1", canonicalBytes(update));
}
export function profileUpdateHashHex(update) {
    return bytesToHex(profileUpdateHash(update));
}
export function profileUpdatePayload(update) {
    return concatBytes(utf8("vox.profile.update.signature.v1"), new Uint8Array([0]), profileUpdateHash(update));
}
export function signProfileUpdate(update, rootSecretKey) {
    return signBytes(profileUpdatePayload(update), rootSecretKey);
}
export function verifyProfileUpdate(update, signature, rootPublicKey) {
    return verifyBytes(profileUpdatePayload(update), signature, rootPublicKey);
}
export function journalEnvelopeHash(envelope) {
    return domainHash("vox.journal.v1", canonicalBytes(envelope));
}
export function journalEnvelopeHashHex(envelope) {
    return bytesToHex(journalEnvelopeHash(envelope));
}
export function journalSigningPayload(envelopeOrHash) {
    const hash = envelopeOrHash instanceof Uint8Array ? envelopeOrHash : journalEnvelopeHash(envelopeOrHash);
    return concatBytes(utf8("vox.journal.signature.v1"), new Uint8Array([0]), hash);
}
export function verifyJournalSignature(envelope, signature, instancePublicKey) {
    return verifyBytes(journalSigningPayload(envelope), signature, instancePublicKey);
}
export function recordDigest(postHashBytes, envelopeHashBytes) {
    return domainHash("vox.record.v1", postHashBytes, envelopeHashBytes);
}
export function domainAssociationPayload(association) {
    return concatBytes(utf8("vox.domain.v1"), new Uint8Array([0]), canonicalBytes(association));
}
export function signDomainAssociation(association, rootSecretKey) {
    return signBytes(domainAssociationPayload(association), rootSecretKey);
}
export function verifyDomainAssociation(association) {
    const { root_signature, ...unsigned } = association;
    return association.agent_id === `vox:agent:${association.identity_public_key}` && verifyBytes(domainAssociationPayload(unsigned), root_signature, association.identity_public_key);
}
export function humanIdFromPublicKey(publicKey) {
    return `vox:human:${bytesToBase64Url(publicKey)}`;
}
export function humanRegistrationPayload(registration) {
    return concatBytes(utf8("vox.human.register.v1"), new Uint8Array([0]), canonicalBytes(registration));
}
export function signHumanRegistration(registration, secretKey) {
    return signBytes(humanRegistrationPayload(registration), secretKey);
}
export function verifyHumanRegistration(registration) {
    const { root_signature, ...unsigned } = registration;
    return registration.human_id === `vox:human:${registration.identity_public_key}` && verifyBytes(humanRegistrationPayload(unsigned), root_signature, registration.identity_public_key);
}
export function commissionRequestHash(intent) {
    return domainHash("vox.commission.request.v1", canonicalBytes(intent));
}
export function commissionRequestHashHex(intent) {
    return bytesToHex(commissionRequestHash(intent));
}
export function commissionRequestIdFromIntent(intent) {
    return `vox:request:${commissionRequestHashHex(intent)}`;
}
export function commissionRequestSigningPayload(intentOrHash) {
    const hash = intentOrHash instanceof Uint8Array ? intentOrHash : commissionRequestHash(intentOrHash);
    return concatBytes(utf8("vox.commission.request.signature.v1"), new Uint8Array([0]), hash);
}
export function signCommissionRequest(intent, secretKey) {
    return signBytes(commissionRequestSigningPayload(intent), secretKey);
}
export function verifyCommissionRequestSignature(intent, signature) {
    return verifyBytes(commissionRequestSigningPayload(intent), signature, intent.human_id.slice("vox:human:".length));
}
//# sourceMappingURL=crypto.js.map